import { useFormikContext } from "formik";
import React, { FC, useState } from "react";
import { ACCOUNT_TYPES } from "../../../constants/account-constants";
import { ENROLL_MOBILE_HID_READERS, ENROLL_MOBILE_PROPRIETERY_HID, ENROLL_MOBILE_PROPRIETERY_STID, ENROLL_MOBILE_STID_READERS } from "../../../constants/enrol-function";
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { MobileAccount } from "../../../models/Account";
import { MultiSelectOption } from "../../../models/MultiSelectOption";
import List from "../../list/list";

interface SelectUsedWithProps {
    onBack: (event: any) => void;
    onNext: (event: any) => void;
    setSelectedUsedFor: (selectedUsedFor: MultiSelectOption) => void;
    account: MobileAccount | undefined;
}

/**
 * SelectUsedWith Component
 * 
 * This component allows users to select the reader types that the mobile credential will be used with.
 * Examples include HID/STid readers or Impro readers.
 * 
 * @param {Function} props.onBack - Callback function triggered when the user clicks on the back button.
 * @param {Function} props.onNext - Callback function triggered when the user clicks on the next button.
 * @param {Function} props.setSelectedUsedFor - Callback function to set the selected reader type.
 * @param {MobileAccount} props.account - The mobile account object containing account type information.
 */
const SelectUsedWith: FC<SelectUsedWithProps> = ({ onBack, onNext, setSelectedUsedFor, account }) => {
    const { translate } = useLocale();
    const [enableNext, setEnableNext] = useState(false);
    const [value, setValue] = useState<string>('');
    const { setFieldValue } = useFormikContext<any>();

    const getOptions = () => {
        if (account?.accountTypeId === ACCOUNT_TYPES.ID_HID_MOBILE_ACCESS.toString()) {
            return [
                { label: translate.byKey(TranslationKeys.hidReaders), value: ENROLL_MOBILE_HID_READERS },
                { label: translate.byKey(TranslationKeys.improMobileReaders), value: ENROLL_MOBILE_PROPRIETERY_HID },
            ];
        }

        return [
            { label: translate.byKey(TranslationKeys.stidReaders), value: ENROLL_MOBILE_STID_READERS },
            { label: translate.byKey(TranslationKeys.improMobileReaders), value: ENROLL_MOBILE_PROPRIETERY_STID },
        ];
    }

    const itemSelected = (item: any) => {
        const selectedItem = item.props['data-item'];
        if (selectedItem) {
            setEnableNext(true);
            setValue(selectedItem.value);
            setSelectedUsedFor({ value: selectedItem.value, label: selectedItem.label })
            setFieldValue("usedWith", selectedItem.value);
            onNext({ task: selectedItem.value, description: selectedItem.label });
        }
    }

    const getItems = (): { value: JSX.Element; isSelected: boolean; }[] => {
        return getOptions().map(item => {
            return {
                value: (
                    <div className="flex flex-row items-center cursor-pointer select-none" data-item={item}>
                        <div className="flex justify-start p-2">{item.label}</div>
                    </div>
                ),
                isSelected: item.value === value
            };
        })
    }

    const onLocalNext = (event: any) => {
        if (enableNext) {
            onNext({ ...event, value });
        }
    }

    return <div className="flex flex-col pt-3 pr-3 h-full relative">
        <List onSelect={(_, item) => itemSelected(item)} isStriped items={getItems()} />

        <div className="flex flex-row justify-end absolute bottom-0 right-0 pr-3 pb-3 ">
            <div className="flex flex-row mt-4">
                <button
                    className="py-1 px-5 secondary secondary-inverse"
                    type="button" onClick={onBack} >{translate.byKey(TranslationKeys.back)}</button>
            </div>
            <div className="flex flex-row justify-end mt-4 pl-3">
                <button
                    className={`py-1 px-5 secondary ${enableNext ? 'secondary-enabled' : 'secondary-disabled cursor-not-allowed'}`}
                    type="button"
                    onClick={e => onLocalNext(e)}>{translate.byKey(TranslationKeys.next)}</button>
            </div>
        </div>
    </div>
}

export default SelectUsedWith;